"use client";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, TextField } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { updateProfile } from "@/store/apps/userData/UserDataSlice";
import ConfirmDialog from "@/app/components/modals/ConfirmDialog";

/**
 * PendingEmailNotifier
 *
 * Persistent ConfirmDialog that surfaces unresolved seeker email states
 * so booking confirmations are never silently dropped:
 *
 *   - State 1 — `email_acknowledged === false` AND `pending_booking_request_id`
 *     exists AND no replacement is pending. The seeker booked but
 *     dismissed SentModal without confirming. Offer Confirm / Change.
 *
 *   - State 2 — `email_pending_replacement === true` AND token is still
 *     valid. The seeker chose a new address but hasn't clicked the link.
 *     Offer Resend / Cancel.
 *
 *   - State 3 — `email_pending_replacement === true` AND token expired.
 *     Same options; copy notes that the link expired.
 *
 * Suppressed while the booking flow is active so it never collides with
 * SentModal or the processing overlay.
 */
export default function PendingEmailNotifier({
  hasOtherNotifications = false,
  onActiveChange = () => {},
}) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const profile = useSelector((state) => state.user.profile);
  const isProcessingBooking = useSelector(
    (state) => state.user.isProcessingBooking,
  );
  const showSentModal = useSelector((state) => state.user.showSentModal);
  const sentModalDismissed = useSelector(
    (state) => state.user.sentModalDismissed,
  );

  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [changeOpen, setChangeOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);

  const pendingReplacement = profile?.email_pending_replacement === true;
  const tokenExpiry = profile?.email_token_expiry || 0;
  const tokenExpired =
    pendingReplacement && tokenExpiry > 0 && tokenExpiry < Date.now();
  const pendingBookingId = profile?.pending_booking_request_id;
  const ackPending =
    profile?.email_acknowledged === false &&
    !pendingReplacement &&
    !!pendingBookingId;

  const state = useMemo(() => {
    if (pendingReplacement && tokenExpired) return "expired";
    if (pendingReplacement) return "pending-link";
    if (ackPending) return "needs-confirm";
    return null;
  }, [pendingReplacement, tokenExpired, ackPending]);

  const visible =
    !!state &&
    sentModalDismissed &&
    !isProcessingBooking &&
    !showSentModal &&
    !hasOtherNotifications;

  useEffect(() => {
    onActiveChange(visible);
  }, [visible, onActiveChange]);

  // Reset transient UI state whenever the underlying state changes so
  // the dialog never shows stale errors or a half-typed new email.
  useEffect(() => {
    setErrorMsg("");
    setChangeOpen(false);
    setNewEmail("");
    setConfirmCancelOpen(false);
  }, [state]);

  if (!profile || !state) return null;

  const handleConfirm = async () => {
    if (!pendingBookingId) return;
    setSubmitting(true);
    setErrorMsg("");
    try {
      const res = await fetch("/api/email/confirm-seeker-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ meetingId: pendingBookingId }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        setErrorMsg(data?.message || "Couldn't confirm. Please try again.");
        return;
      }
      dispatch(
        updateProfile({
          email_acknowledged: true,
          email_verified: true,
          email_pending_replacement: false,
          pending_booking_request_id: null,
        }),
      );
    } catch {
      setErrorMsg("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendChange = async () => {
    const trimmed = newEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setErrorMsg("Please enter a valid email address.");
      return;
    }
    if (!pendingBookingId) {
      setErrorMsg("No pending booking found.");
      return;
    }
    setSubmitting(true);
    setErrorMsg("");
    try {
      const res = await fetch("/api/email/confirm-seeker-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          meetingId: pendingBookingId,
          newEmail: trimmed,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        setErrorMsg(data?.message || "Couldn't send link. Please try again.");
        return;
      }
      dispatch(
        updateProfile({
          email_pending_replacement: true,
          pending_email: trimmed,
          email_token_expiry: Date.now() + 86400000,
        }),
      );
      setChangeOpen(false);
      setNewEmail("");
    } catch {
      setErrorMsg("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setSubmitting(true);
    setErrorMsg("");
    try {
      const res = await fetch("/api/email/resend-confirmation", {
        method: "POST",
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        setErrorMsg(data?.message || "Couldn't resend. Please try again.");
        return;
      }
      dispatch(
        updateProfile({
          email_token_expiry: data.expiry || Date.now() + 86400000,
        }),
      );
    } catch {
      setErrorMsg("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelChange = async () => {
    setSubmitting(true);
    setErrorMsg("");
    try {
      const res = await fetch("/api/email/cancel-email-change", {
        method: "POST",
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        setErrorMsg(data?.message || "Couldn't cancel. Please try again.");
        return;
      }

      // Server cancelled the replacement AND tried to confirm the
      // original email + flush the deferred booking confirmation.
      // Reflect whichever branch the server took:
      //   - confirmed:true  → original email verified, booking email sent
      //                       (or queued); banner closes entirely.
      //   - confirmed:false → Hunter rejected the original email; banner
      //                       flips to `needs-confirm` so the seeker can
      //                       try again or change to a different address.
      if (data.confirmed) {
        dispatch(
          updateProfile({
            email_pending_replacement: false,
            pending_email: null,
            email_token_expiry: null,
            email_acknowledged: true,
            email_verified: true,
            pending_booking_request_id: data.sent
              ? null
              : profile?.pending_booking_request_id,
          }),
        );
      } else {
        dispatch(
          updateProfile({
            email_pending_replacement: false,
            pending_email: null,
            email_token_expiry: null,
            email_acknowledged: false,
          }),
        );
        // The state-change effect will clear errorMsg on the transition
        // to `needs-confirm`, so don't bother surfacing the Hunter
        // rejection here — the seeker will see it again if they click
        // Confirm on the original email and Hunter rejects a second time.
      }

      setConfirmCancelOpen(false);
    } catch {
      setErrorMsg("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ---------------- Primary dialog content ----------------
  const dialog = (() => {
    if (state === "needs-confirm") {
      return {
        title: "Confirm your email address",
        body: (
          <>
            We&apos;ll send your booking confirmation to{" "}
            <strong>{profile.email}</strong>. Is this the right address?
            {errorMsg && (
              <Box
                sx={{
                  color: theme.palette.error.main,
                  fontSize: "0.85rem",
                  mt: 1.5,
                }}
              >
                {errorMsg}
              </Box>
            )}
          </>
        ),
        confirmButtonText: "Yes, it's correct",
        ignoreButtonText: "Change email",
        onConfirm: handleConfirm,
        onIgnore: () => {
          setErrorMsg("");
          setChangeOpen(true);
        },
      };
    }
    if (state === "pending-link") {
      return {
        title: "Check your inbox",
        body: (
          <>
            We sent a verification link to{" "}
            <strong>{profile.pending_email}</strong>. Click it to receive your
            booking confirmation.
            {errorMsg && (
              <Box
                sx={{
                  color: theme.palette.error.main,
                  fontSize: "0.85rem",
                  mt: 1.5,
                }}
              >
                {errorMsg}
              </Box>
            )}
          </>
        ),
        confirmButtonText: "Resend link",
        ignoreButtonText: "Cancel change",
        onConfirm: handleResend,
        onIgnore: () => setConfirmCancelOpen(true),
      };
    }
    // expired
    return {
      title: "Verification link expired",
      body: (
        <>
          The verification link for <strong>{profile.pending_email}</strong> has
          expired. Send a new link or cancel the change to keep your original
          email.
          {errorMsg && (
            <Box
              sx={{
                color: theme.palette.error.main,
                fontSize: "0.85rem",
                mt: 1.5,
              }}
            >
              {errorMsg}
            </Box>
          )}
        </>
      ),
      confirmButtonText: "Resend link",
      ignoreButtonText: "Cancel change",
      onConfirm: handleResend,
      onIgnore: () => setConfirmCancelOpen(true),
    };
  })();

  return (
    <>
      <ConfirmDialog
        open={visible && !changeOpen && !confirmCancelOpen}
        onClose={() => {}}
        onConfirm={dialog.onConfirm}
        onIgnore={dialog.onIgnore}
        title={dialog.title}
        body={dialog.body}
        confirmButtonText={dialog.confirmButtonText}
        ignoreButtonText={dialog.ignoreButtonText}
        showIgnoreButton={true}
        showConfirmButton={true}
        showCloseIcon={false}
        disableBackdropClick={true}
        disableEscapeKeyDown={true}
        isProcessing={submitting}
        processingText="Saving..."
      />

      <ConfirmDialog
        open={changeOpen}
        onClose={() => {
          if (submitting) return;
          setChangeOpen(false);
          setErrorMsg("");
          setNewEmail("");
        }}
        onConfirm={handleSendChange}
        onIgnore={() => {
          if (submitting) return;
          setChangeOpen(false);
          setErrorMsg("");
          setNewEmail("");
        }}
        title="Use a different email"
        confirmButtonText="Send link"
        ignoreButtonText="Cancel"
        showIgnoreButton={true}
        showConfirmButton={true}
        showCloseIcon={true}
        isProcessing={submitting}
        processingText="Sending..."
        disableConfirm={!newEmail.trim()}
        body={
          <>
            Enter the email where you want to receive your booking confirmation.
            We&apos;ll send a verification link to confirm it.
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                size="small"
                type="email"
                placeholder="you@example.com"
                value={newEmail}
                onChange={(e) => {
                  setNewEmail(e.target.value);
                  if (errorMsg) setErrorMsg("");
                }}
                disabled={submitting}
                autoFocus
              />
              {errorMsg && (
                <Box
                  sx={{
                    color: theme.palette.error.main,
                    fontSize: "0.85rem",
                    mt: 1,
                  }}
                >
                  {errorMsg}
                </Box>
              )}
            </Box>
          </>
        }
      />

      <ConfirmDialog
        open={confirmCancelOpen}
        onClose={() => {
          if (submitting) return;
          setConfirmCancelOpen(false);
        }}
        onConfirm={handleCancelChange}
        onIgnore={() => {
          if (submitting) return;
          setConfirmCancelOpen(false);
        }}
        title="Cancel email change?"
        confirmButtonText="Yes, cancel"
        ignoreButtonText="Keep waiting"
        showIgnoreButton={true}
        showConfirmButton={true}
        showCloseIcon={true}
        isProcessing={submitting}
        processingText="Cancelling..."
        body={
          <>
            Cancelling discards the verification link sent to{" "}
            <strong>{profile.pending_email}</strong>. Your original email{" "}
            <strong>{profile.email} </strong> stays on file and you&apos;ll need
            to confirm or change it again.
          </>
        }
      />
    </>
  );
}
